import { useState, useEffect, useRef, RefObject } from 'react';
import { Flex, Tab, SrOnly } from '@servicetitan/anvil2';
import { Calculation, PaymentFrequency } from '@servicetitan/financing-api-client';
import { usePerTabHorizontalScroll } from './usePerTabHorizontalScroll';

// Define the specific payment frequencies used in the UI
type UIPaymentFrequency =
    | PaymentFrequency.Weekly
    | PaymentFrequency.BiWeekly
    | PaymentFrequency.Monthly;

const TAB_FREQUENCIES = [
    PaymentFrequency.Weekly,
    PaymentFrequency.BiWeekly,
    PaymentFrequency.Monthly,
] as const satisfies readonly UIPaymentFrequency[];

const TAB_LABELS: Record<UIPaymentFrequency, string> = {
    [PaymentFrequency.Weekly]: 'Weekly',
    [PaymentFrequency.BiWeekly]: 'Bi-Weekly',
    [PaymentFrequency.Monthly]: 'Monthly',
};

const INITIAL_SCROLL_BY_FREQUENCY: Record<string, number> = {
    [PaymentFrequency.Weekly]: 0,
    [PaymentFrequency.BiWeekly]: 0,
    [PaymentFrequency.Monthly]: 0,
};

export interface PaymentFrequencyTabsProps {
    plans: Calculation[];
    onFrequencyChange: (frequency: PaymentFrequency) => void;
    scrollContainerRef?: RefObject<HTMLDivElement | null>;
}

/**
 * Always displays tabs for Weekly, Bi-Weekly, and Monthly payment frequencies
 * Tracks the selected frequency and notifies parent for rendering decisions
 * Shows "no plans available" message when no plans match the selected frequency
 * Manages independent scroll positions for each tab
 */
export const PaymentFrequencyTabs = ({
    plans,
    onFrequencyChange,
    scrollContainerRef,
}: PaymentFrequencyTabsProps) => {
    const [selectedFrequency, setSelectedFrequency] = useState<UIPaymentFrequency>(
        PaymentFrequency.Weekly,
    );
    const selectedIndex = TAB_FREQUENCIES.indexOf(selectedFrequency);
    const tabIndex = selectedIndex === -1 ? 0 : selectedIndex;

    const hasInitialized = useRef(false);

    const { beginTabTransition } = usePerTabHorizontalScroll(
        scrollContainerRef,
        selectedFrequency,
        {
            initialScrollLeftByKey: INITIAL_SCROLL_BY_FREQUENCY,
        },
    );

    const handleFrequencyChange = (frequency: UIPaymentFrequency) => {
        beginTabTransition(frequency, () => {
            setSelectedFrequency(frequency);
            onFrequencyChange(frequency);
        });
    };

    const handleIndexChange = (index: number) => {
        const frequency = TAB_FREQUENCIES[index];
        if (frequency) {
            handleFrequencyChange(frequency);
        }
    };

    useEffect(() => {
        if (plans.length > 0 && !hasInitialized.current) {
            hasInitialized.current = true;
            onFrequencyChange(selectedFrequency);
        }
    }, [plans.length, onFrequencyChange, selectedFrequency]);

    return (
        <Flex direction="column" gap="3">
            <Tab
                index={tabIndex}
                onIndexChange={handleIndexChange}
                fill
                style={{ gap: 'revert', width: '100%' }}
            >
                <Tab.List>
                    {TAB_FREQUENCIES.map(frequency => (
                        <Tab.Button
                            key={frequency}
                            id={`payment-freq-tab-${frequency}`}
                            controls={`payment-freq-panel-${frequency}`}
                        >
                            {TAB_LABELS[frequency]}
                        </Tab.Button>
                    ))}
                </Tab.List>
                {TAB_FREQUENCIES.map(frequency => (
                    <Tab.Panel
                        key={frequency}
                        id={`payment-freq-panel-${frequency}`}
                        style={{ padding: 0, margin: 0, minHeight: 0 }}
                    >
                        <SrOnly>
                            {TAB_LABELS[frequency]} payment frequency. Plans are listed below.
                        </SrOnly>
                    </Tab.Panel>
                ))}
            </Tab>
        </Flex>
    );
};
